import { useState } from 'react';

import { InfoModal } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, Button, SxProps, TextField, Theme, Typography } from '@mui/material';

const STRINGS = appStrings.modules.myColleges.compare;

type NameDialogProps = Readonly<{
  id: string;
  title: string;
  saving: boolean;
  failed: boolean;
  onClose: () => void;
  onSubmit: (name: string) => void;
}>;

export const NameDialog = ({ id, title, saving, failed, onClose, onSubmit }: NameDialogProps) => {
  const [name, setName] = useState('');
  const trimmed = name.trim();

  return (
    <InfoModal
      open
      onClose={onClose}
      width="xs"
      featuredIcon={{ iconName: 'Scales01', size: 'lg', color: 'Colors/Foreground/fg-secondary' }}
      headerContent={
        <Typography id={`${id}-title`} variant="text-lg-semibold" component="h2">
          {title}
        </Typography>
      }
      bodyContainerSx={bodyContainerSx}
      bodyContent={
        <Box
          id={id}
          component="form"
          sx={bodySx}
          onSubmit={(event) => {
            event.preventDefault();
            if (trimmed && !saving) onSubmit(trimmed);
          }}
        >
          <TextField
            id={`${id}-name-input`}
            label={STRINGS.nameLabel}
            placeholder={STRINGS.namePlaceholder}
            value={name}
            onChange={(event) => setName(event.target.value)}
            inputProps={{ maxLength: 100 }}
            fullWidth
            autoFocus
          />
          {failed && (
            <Typography id={`${id}-failed`} variant="text-sm-medium" sx={errorSx}>
              {STRINGS.createFailed}
            </Typography>
          )}
          <Box sx={actionsSx}>
            <Button
              id={`${id}-cancel`}
              hierarchy="Secondary gray"
              size="lg"
              fullWidth
              onClick={onClose}
            >
              {STRINGS.cancel}
            </Button>
            <Button
              id={`${id}-save`}
              type="submit"
              hierarchy="Primary"
              size="lg"
              fullWidth
              disabled={!trimmed || saving}
            >
              {STRINGS.save}
            </Button>
          </Box>
        </Box>
      }
    />
  );
};

const bodyContainerSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  zIndex: 1,
  px: backrs.spacing['spacing-3xl'],
  pb: backrs.spacing['spacing-3xl'],
});

const bodySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-2xl'],
  paddingTop: backrs.spacing['spacing-3xl'],
});

const actionsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-lg'],
});

const errorSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-error-primary'],
});
